import { useEffect, useRef, useState } from 'react';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Table from '../components/table/table';
import Input from '../components/input/Input';
import Button from '../components/button/Button';
import Dropdown from '../components/dropdown/DropDown';
import DatePicker from '../components/datepicker/DatePicker';
import Spinner from '../components/Spinner/spinner';
import { ITableData } from '../types/interfaces';
import { hasContent } from '../utils/Utils';
import { getOverdueColumns, OverdueData } from '../utils/overdueColumns';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { getOverdue, getOverdueFilters } from '../services/GuaranteesService';
import OverdueStatusModal from '../components/modals/OverdueStatusModal';
import { Toast } from '../components/toast/toast';

interface FilterOption {
  id: string | number;
  name: string;
}

interface OverdueFilters {
  creditors: FilterOption[];
  types: FilterOption[];
}

const Overdue = () => {
  const [overdueData, setOverdueData] = useState<OverdueData[]>([]);
  const [tableData, setTableData] = useState<ITableData>({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
  });
  const [page, setPage] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedCreditor, setSelectedCreditor] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [filters, setFilters] = useState<OverdueFilters>({
    creditors: [],
    types: [],
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedOverdueItem, setSelectedOverdueItem] = useState<OverdueData | null>(null);

  const lastRequest = useRef(0);

  // Cargar filtros al montar el componente
  useEffect(() => {
    loadFilters();
  }, []);

  // Cargar datos cuando cambian los filtros o la página
  useEffect(() => {
    fetchOverdueData();
    // eslint-disable-next-line
  }, [page, selectedDate, selectedCreditor, selectedType]);

  const loadFilters = async () => {
    try {
      const response = await getOverdueFilters();
      console.log('Filtros recibidos:', response);
      if (response && 'data' in response && response.data) {
        const newFilters = {
          creditors: response.data.creditors || [],
          types: response.data.types || [],
        };
        console.log('Filtros a setear:', newFilters);
        setFilters(newFilters);
      }
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };

  // Función para detectar si es un CUIT y limpiar los guiones
  const cleanSearchText = (text: string): string => {
    if (!text) return text;
    
    // Regex para detectar formato de CUIT: XX-XXXXXXXX-X o solo números con guiones
    const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
    const cuitPatternPartial = /^\d+-\d+-?\d*$/; // Para CUITs parciales con guiones
    
    // Si coincide con el patrón de CUIT, quitar los guiones
    if (cuitPattern.test(text) || cuitPatternPartial.test(text)) {
      return text.replace(/-/g, '');
    }
    
    // Si es solo números y guiones (posible CUIT sin formato completo)
    if (/^[\d-]+$/.test(text)) {
      return text.replace(/-/g, '');
    }
    
    // Si no, devolver el texto original (es una razón social)
    return text;
  };

  const fetchOverdueData = async () => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    try {
      const response = await getOverdue(
        page,
        10,
        selectedDate,
        selectedCreditor,
        selectedType,
        cleanSearchText(searchText)
      );

      if (lastRequest.current !== requestId) return;

      if (hasContent(response)) {
        setTableData({
          size: response.data.size,
          page: response.data.page,
          totalElements: response.data.total_elements,
          totalPages: response.data.total_pages,
        });

        setOverdueData(response.data.content || []);
      } else {
        setOverdueData([]);
        setTableData({ page: 0, size: 10, totalElements: 0, totalPages: 0 });
      }
    } catch (error) {
      console.error('Error fetching overdue data:', error);
      setOverdueData([]);
      setTableData({ page: 0, size: 10, totalElements: 0, totalPages: 0 });
    }

    setIsLoading(false);
    setIsSearching(false);
  };

  const handleChangePage = (data: number) => {
    if (data < 0 || data >= tableData.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleSearch = (keyCode?: number) => {
    if (keyCode && keyCode !== 13) return;
    setIsSearching(true);
    setPage(0);
    fetchOverdueData();
  };

  const handleOpenModal = (row: OverdueData) => {
    // Convertir el nombre del tipo al ID antes de enviarlo al modal
    const typeFilter = filters.types.find((t) => t.name === row.type);
    const typeId = typeFilter ? String(typeFilter.id) : row.type;
    
    const itemWithTypeId = {
      ...row,
      type: typeId, // Reemplazar el nombre con el ID
    };
    
    setSelectedOverdueItem(itemWithTypeId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOverdueItem(null);
  };

  const handleModalSuccess = () => {
    fetchOverdueData();
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setPage(0);
  };

  const handleCreditorChange = (value: string) => {
    if (value === 'Todos los acreedores') {
      setSelectedCreditor('');
    } else {
      const selectedFilter = filters.creditors.find((c) => c.name === value);
      setSelectedCreditor(selectedFilter ? String(selectedFilter.id) : '');
    }
    setPage(0);
  };

  const handleTypeChange = (value: string) => {
    if (value === 'Todos los instrumentos') {
      setSelectedType('');
    } else {
      const selectedFilter = filters.types.find((t) => t.name === value);
      setSelectedType(selectedFilter ? String(selectedFilter.id) : '');
    }
    setPage(0);
  };

  const columns = getOverdueColumns({ handleOpenModal });

  // Obtener el mes y año actual para el date picker
  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  console.log('Filtros en render:', filters);
  console.log('Creditors:', filters.creditors);
  console.log('Types:', filters.types);

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Vencimientos
          </H>
        </Card>
      </Card>

      {/* Filtros */}
      <Card display='flex' margin={{ top: '3', bottom: '3' }} gap='16px' alignItems='end'>
        <Card width='200px'>
          <DatePicker
            monthly={true}
            displayFormat="MMMM yyyy"
            maxDate={new Date()}
            defaultValue={getCurrentDate()}
            onChange={(date: Date | string) => {
              if (!date) return;
              
              let year: number;
              let month: string;
              let day: string;
              
              if (typeof date === 'string') {
                // Si date es string en formato YYYY-MM, agregamos el día
                const parts = date.split('-');
                year = parseInt(parts[0]);
                month = parts[1];
                day = '01'; // Primer día del mes
              } else {
                // Si date es objeto Date
                year = date.getFullYear();
                month = String(date.getMonth() + 1).padStart(2, '0');
                day = String(date.getDate()).padStart(2, '0');
              }
              
              handleDateChange(`${year}-${month}-${day}`);
            }}
            disabled={false}
          />
        </Card>

        <Card width='200px'>
          <Dropdown
            id='creditor-filter'
            title={
              filters.creditors.find((c) => String(c.id) === selectedCreditor)?.name ||
              'Todos los acreedores'
            }
            items={[
              { label: 'Todos los acreedores' },
              ...filters.creditors.map((c) => ({ label: c.name })),
            ]}
            onChange={handleCreditorChange}
            size='md'
          />
        </Card>

        <Card width='230px'>
          <Dropdown
            id='type-filter'
            title={
              filters.types.find((t) => String(t.id) === selectedType)?.name ||
              'Todos los instrumentos'
            }
            items={[
              { label: 'Todos los instrumentos' },
              ...filters.types.map((t) => ({ label: t.name })),
            ]}
            onChange={handleTypeChange}
            size='md'
          />
        </Card>

        <Card width='400px' display='flex' gap='16px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder='Buscar razón social o CUIT'
            onChange={(e) => setSearchText(e.target.value)}
            onKeyup={(e) => handleSearch(e.keyCode)}
          />
          <Button variant='tertiary' size='sm' onClick={() => handleSearch(13)}>
            {isSearching ? <Spinner size='sm' variant='primary' /> : 'Buscar'}
          </Button>
        </Card>
      </Card>

      {/* Tabla */}
      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : overdueData.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={overdueData}
            tableData={tableData}
            handleChangePage={(e: any) => handleChangePage(e)}
          />
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No encontramos vencimientos.
          </P>
          <P variant='primary' align='center'>
            Intenta ajustar los filtros de búsqueda.
          </P>
        </Card>
      )}

      {/* Modal */}
      <OverdueStatusModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        overdueItem={selectedOverdueItem}
        onSuccess={handleModalSuccess}
      />
      
      {/* Toast Container */}
      <Toast id='overdue-toast' />
    </Card>
  );
};

export default Overdue;
